/**
 * DEV-ONLY: Color field metadata + applicability for Layout Inspector (classic).
 * Write-back uses the same inline `element.style` temp overrides as numeric TEMP EDITS.
 */

/** Keys stored on `overrides` / session `tempEdits` — keep in sync with apply/clear in LayoutInspectorGlobal. */
export const COLOR_OVERRIDE_KEYS = [
  "color",
  "backgroundColor",
  "borderColor",
  "outlineColor",
  "textDecorationColor",
  "fill",
  "stroke",
];

/**
 * @typedef {{ key: string; label: string; styleProp: string; computedValue: string }} ColorLayoutField
 */

const TAG = (el) => (el?.tagName || "").toLowerCase();

function isTextLikeInput(el) {
  if (TAG(el) !== "input") return true;
  const t = (el.getAttribute("type") || "text").toLowerCase();
  const noTextColor = new Set([
    "hidden",
    "image",
    "file",
    "radio",
    "checkbox",
    "range",
    "color",
    "submit",
    "button",
    "reset",
  ]);
  return !noTextColor.has(t);
}

function isSvgGraphic(el) {
  return el?.namespaceURI === "http://www.w3.org/2000/svg";
}

function hasVisibleBorder(cs) {
  const w = ["Top", "Right", "Bottom", "Left"].map((s) => parseFloat(cs[`border${s}Width`]) || 0);
  if (w.some((n) => n > 0)) return true;
  return ["Top", "Right", "Bottom", "Left"].some((s) => {
    const st = cs[`border${s}Style`];
    return st && st !== "none";
  });
}

function hasVisibleOutline(cs) {
  const w = parseFloat(cs.outlineWidth) || 0;
  const st = cs.outlineStyle;
  return w > 0 && st && st !== "none";
}

function hasTextDecoration(cs) {
  const line = cs.textDecorationLine || cs.webkitTextDecorationLine || "";
  if (!line || line === "none") return false;
  return line
    .split(/\s+/)
    .map((x) => x.trim())
    .some((x) => x && x !== "none");
}

const DEFS = [
  {
    key: "color",
    label: "Text color",
    styleProp: "color",
    isApplicable(el, cs) {
      const t = TAG(el);
      if (["img", "br", "hr", "col", "wbr", "area", "param", "source", "track", "canvas", "video"].includes(t)) {
        return false;
      }
      return isTextLikeInput(el);
    },
  },
  {
    key: "backgroundColor",
    label: "Background",
    styleProp: "backgroundColor",
    isApplicable(el, cs) {
      const t = TAG(el);
      if (["img", "video", "canvas"].includes(t)) return false;
      return true;
    },
  },
  {
    key: "borderColor",
    label: "Border color",
    styleProp: "borderColor",
    isApplicable(el, cs) {
      return hasVisibleBorder(cs);
    },
  },
  {
    key: "outlineColor",
    label: "Outline color",
    styleProp: "outlineColor",
    isApplicable(el, cs) {
      return hasVisibleOutline(cs);
    },
  },
  {
    key: "textDecorationColor",
    label: "Decoration color",
    styleProp: "textDecorationColor",
    isApplicable(el, cs) {
      return hasTextDecoration(cs);
    },
  },
  {
    key: "fill",
    label: "Fill",
    styleProp: "fill",
    isApplicable(el, cs) {
      if (!isSvgGraphic(el)) return false;
      const t = TAG(el);
      return ["path", "circle", "rect", "ellipse", "line", "polyline", "polygon", "text", "use", "g", "svg"].includes(t);
    },
  },
  {
    key: "stroke",
    label: "Stroke",
    styleProp: "stroke",
    isApplicable(el, cs) {
      if (!isSvgGraphic(el)) return false;
      const t = TAG(el);
      if (!["path", "circle", "rect", "ellipse", "line", "polyline", "polygon", "text", "use", "g", "svg"].includes(t)) {
        return false;
      }
      const sw = parseFloat(cs.strokeWidth) || 0;
      return sw > 0 || (cs.stroke && cs.stroke !== "none");
    },
  },
];

/** For apply/clear of inline styles on the selected element. */
export const COLOR_STYLE_APPLY = DEFS.map((d) => ({ key: d.key, styleProp: d.styleProp }));

/**
 * Best-effort hex (#rrggbb) for `<input type="color">`. Returns null if not representable (e.g. `currentColor`, `lab()`, unparseable).
 * @param {string} cssColor
 * @returns {string | null}
 */
export function normalizeCssColorToHex6(cssColor) {
  if (cssColor == null || typeof cssColor !== "string") return null;
  const v = cssColor.trim();
  if (v === "" || v === "transparent") return null;
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    const r = v[1];
    const g = v[2];
    const b = v[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#000000";
    ctx.fillStyle = v;
    const out = ctx.fillStyle;
    if (typeof out === "string" && /^#[0-9a-f]{6}$/i.test(out)) return out.toLowerCase();
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * @param {Element | null} el
 * @returns {ColorLayoutField[]}
 */
export function getColorLayoutFields(el) {
  if (!el || !el.getBoundingClientRect || !window.getComputedStyle) return [];
  let cs;
  try {
    cs = window.getComputedStyle(el);
  } catch {
    return [];
  }
  const out = [];
  for (const def of DEFS) {
    if (!def.isApplicable(el, cs)) continue;
    let raw = "";
    try {
      raw = cs[def.styleProp] != null ? String(cs[def.styleProp]) : "";
    } catch {
      raw = "";
    }
    out.push({
      key: def.key,
      label: def.label,
      styleProp: def.styleProp,
      computedValue: raw.trim(),
    });
  }
  return out;
}
